import React, { memo, useEffect, useState } from 'react';

import bell from 'assets/images/svg/bell.svg';
import faq from 'assets/images/svg/faq.svg';
import support from 'assets/images/svg/support.svg';
import { CrossIcon, CustomLink, Image, TertiaryButton } from 'components/common';
import { useLoggedInStatus, useProfileData, useToggle } from 'hooks';

import { RouteDictionary } from '../../../utils';
import { ymAsync } from '../../../utils/functions/ymWrapper';
import { Button, Hamburger, SecondaryButton } from '../../common';
import { SecondaryButtonColor } from '../../common/buttons/decorators/SecondaryButton/SecondaryButton';
import { TertiaryButtonColor } from '../../common/buttons/decorators/TertiaryButton/TertiaryButton';
import { LogoImage } from '../../common/images/types';
import { LogoType } from '../../common/images/types/LogoImage';
import { HamburgerColor } from '../../common/utils/Hamburger/Hamburger';
import { useWindowWidth } from '../../pages/detailed-project/hooks';
import { ProfileManager } from '../../user';
import ProfileMenu from '../../user/profile/ProfileMenu/ProfileMenu';
import { Container, LenderNavigation } from '../index';
import MobileNavigation from '../MobileNavigation';

import './Header.scoped.scss';

type Props = {
  isPublic?: boolean;
};

const Header = ({ isPublic }: Props) => {
  const [active, toggle, setActive] = useToggle();
  const profileData = useProfileData();
  const isLoggedIn = useLoggedInStatus();
  const isMobile = useWindowWidth(992);
  const [showProfileManager, setShowProfileManager] = useState<boolean>(false);
  useEffect(() => {
    if (active) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [active]);
  const handleWrapperClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setActive(false);
    }
  };

  const ymClick = (event: any) => {
    event.preventDefault();
    ymAsync('investor-enter-button');
    window.location.href = RouteDictionary.ENTER;
  };
  return (
    <div
      className={`header__wrapper ${active ? 'header__wrapper__active' : ''}`}
      onClick={handleWrapperClick}
    >
      <header className="header">
        <Container>
          <nav className="header__navigation">
            <div className="header__navigation__left">
              {isLoggedIn && isMobile ? (
                <>
                  <ProfileMenu />
                  <div className="header__mobile__notifications">
                    <div className="header__navigation__center">
                      <div className="header__notifications">
                        <CustomLink
                          to={RouteDictionary.NOTIFICATIONS}
                          className="header__notifications__container"
                        >
                          <Image src={bell} alt="notifications" />
                        </CustomLink>
                        {profileData.newNotificationCount > 0 && (
                          <span className="header__notifications__notification-count">
                            {profileData.newNotificationCount}
                          </span>
                        )}
                      </div>
                      <CustomLink to={RouteDictionary.FAQ} className="header__faq">
                        <Image src={faq} alt="faq" className="header__faq" />
                      </CustomLink>
                      {profileData.deal && (
                        <div className="header__support">
                          <Image
                            src={support}
                            alt="support"
                            className="header__support"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={() => setShowProfileManager((prev) => !prev)}
                          />
                          {showProfileManager && (
                            <ProfileManager
                              deal={profileData.deal}
                              visible={showProfileManager}
                              setVisible={setShowProfileManager}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    {profileData.newNotificationCount > 0 && (
                      <span className="header__mobile__notifications__notification-count">
                        {profileData.newNotificationCount}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="header__logo-container">
                  <CustomLink className="index-link" to={RouteDictionary.INDEX}>
                    <LogoImage className="header__logo" type={LogoType.HEADER_MOBILE} />
                  </CustomLink>
                </div>
              )}
              {isPublic && (
                <div className="header__projects">
                  <CustomLink to={RouteDictionary.PRIMARY_MARKET} className="header__projects-link">
                    <Hamburger rows={3} color={HamburgerColor.GREEN} />
                    <div>Проекты</div>
                  </CustomLink>
                </div>
              )}
            </div>
            {isLoggedIn && !isMobile && (
              <div className="header__navigation__center">
                <div className="header__notifications">
                  <CustomLink
                    to={RouteDictionary.NOTIFICATIONS}
                    className="header__notifications__container"
                  >
                    <Image src={bell} alt="notifications" />
                  </CustomLink>
                  {profileData.newNotificationCount > 0 && (
                    <span className="header__notifications__notification-count">
                      {profileData.newNotificationCount}
                    </span>
                  )}
                </div>
                <CustomLink to={RouteDictionary.FAQ} className="">
                  <Image src={faq} alt="faq" />
                </CustomLink>
                {profileData.deal && (
                  <div className="header__support">
                    <Image
                      src={support}
                      alt="support"
                      className="header__support"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={() => setShowProfileManager((prev) => !prev)}
                    />
                    {showProfileManager && (
                      <ProfileManager
                        deal={profileData.deal}
                        visible={showProfileManager}
                        setVisible={setShowProfileManager}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="header__navigation__right">
              {!isLoggedIn && (
                <div className="header__auth-menu">
                  Войти как
                  <SecondaryButton color={SecondaryButtonColor.GREEN}>
                    <CustomLink
                      to={RouteDictionary.ENTER}
                      type="investor-enter-button"
                      onClick={(event) => ymClick(event)}
                    >
                      Инвестор
                    </CustomLink>
                  </SecondaryButton>
                  или
                  <SecondaryButton color={SecondaryButtonColor.ORANGE}>
                    <Button>Заемщик</Button>
                  </SecondaryButton>
                </div>
              )}
              {isLoggedIn && (
                <div className="header__user-menu">
                  <div className="header__profile">
                    <ProfileMenu />
                  </div>
                </div>
              )}
              <div className="header__mobile-open">
                {active ? (
                  <CrossIcon
                    preset="success"
                    onClick={toggle}
                    className="header__mobile-open__cross"
                    title="Close"
                  />
                ) : (
                  <Hamburger onClick={toggle} rows={3} />
                )}
              </div>
            </div>
          </nav>
          {isLoggedIn && (
            <>
              {active && (
                <div className="mobile-navigation__money">
                  <TertiaryButton expanded color={TertiaryButtonColor.GREEN}>
                    <CustomLink to={RouteDictionary.DEPOSIT}>Пополнить</CustomLink>
                  </TertiaryButton>
                  <TertiaryButton expanded color={TertiaryButtonColor.WHITE_GREEN}>
                    <CustomLink to={RouteDictionary.WITHDRAW}>Вывести</CustomLink>
                  </TertiaryButton>
                </div>
              )}
              <LenderNavigation />
            </>
          )}
          <MobileNavigation active={active} onNavigated={() => setActive(false)} />
        </Container>
      </header>
    </div>
  );
};

export default memo(Header);
